import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { formatDistance } from 'date-fns';
import { DirtinessService } from 'src/core/services/dirtiness.service';
import { MedicineService } from 'src/core/services/medicine.service';
import { PermissionsService } from 'src/core/services/permissions.service';
import { DosageType, DosageUnit, MedicineDosagePackageType, MedicineDosageType, PackageType, Medicine, EditMedicinePayload } from 'src/interfaces/medicine';

@Component({
  selector: 'app-medicine-view',
  templateUrl: './medicine-view.component.html',
  styleUrls: ['./medicine-view.component.scss']
})
export class MedicineViewComponent implements OnInit {

  medicineForm = this._formBuilder.group({
    name: ['', Validators.required],
    description: ['', Validators.required],
    barcodePrefix: ['', Validators.required],
    activeIngredient: ['', Validators.required],
    activeIngredientNappyCode: ['', Validators.required],
    scheduleStatus: ['', Validators.required],
    canOrder: [false] 
  });

  scheduleStatuses: Array<number> = [0, 1, 2, 3, 4, 5, 6, 7, 8];

  dosageUnitForm = this._formBuilder.group({
    dosageUnitID: ['', Validators.required]
  });

  dosageTypeForm = this._formBuilder.group({
    dosageTypeID: ['', Validators.required]
  });

  packageTypeForm = this._formBuilder.group({
    packageTypeID: ['', Validators.required]
  });



  medicineDosageTypesForm = this._formBuilder.group({
    defaultPrice: ['', Validators.required],
    additionalInformation: ['', Validators.required],
    usageInstructions: ['', Validators.required],
    storageDirections: ['', Validators.required],
    specialInstructions: ['', Validators.required],
    dosageStrength: ['', Validators.required]
  });

  medicineDosagePackageTypeForm = this._formBuilder.group({
    containedQuantity: ['', Validators.required],
  });

  medicine: Medicine | null = null;
  dosageUnit: DosageUnit | null = null;
  dosageTypes: DosageType[] = [];
  dosageUnits: DosageUnit[] = [];
  packageTypes: PackageType[] = [];
  medicineDosageTypes: MedicineDosageType[] = [];
  medicineDosagePackageTypes: MedicineDosagePackageType[] = [];
  editMode: boolean = false;

  constructor(
    private _formBuilder: FormBuilder,
    private _medicineService: MedicineService,
    private _route: ActivatedRoute,
    private _dirtinessService: DirtinessService,
    private _router: Router,
    public readonly perm: PermissionsService,
  ) { }

  ngOnInit(): void {
    this._route
      .data
      .subscribe(data => {
        this.medicine = data.medicine.medicine;
        this.medicineDosageTypes = data.medicine.medicineDosageTypes;
        this.dosageUnits = data.medicine.dosageUnits;
        this.dosageTypes = data.medicine.dosageTypes;
        this.packageTypes = data.medicine.packageTypes;
        this.updateView(this.medicine, this.medicineDosageTypes);
      });

    this.medicineForm
      .valueChanges
      .subscribe(value => {
        this._dirtinessService.setDirty(true);
      });
  }

  updateView(medicine: Medicine | null, medicineDosageTypes: MedicineDosageType[]) {
    if (!medicine) return;
    this.medicineForm
      .patchValue({
        name: medicine.name,
        description: medicine.description,
        barcodePrefix: medicine.barcode_prefix,
        activeIngredient: medicine.active_ingredient,
        activeIngredientNappyCode: medicine.active_ingredient_nappy_code,
        scheduleStatus: medicine.schedule_status,
        canOrder: medicine.can_be_ordered
      })
    this._dirtinessService.setDirty(false);
  }

  async refreshMedicineDosageTypes(updated: boolean) {

    if (updated && this.medicine)
      this.medicineDosageTypes = await this._medicineService
        .getMedicineDosageTypes(this.medicine.id, 1, 10, '');
  }

  async createMedicineDosageType() {
    if (!this.medicine) return;
    const newMedicineDosageType: MedicineDosageType = await this._medicineService
      .openMedicineDosageTypeCreateDialog(
        "New medicine dosage type",
        "Create new medicine dosage type",
        this.medicine.id,
        this.dosageTypes,
        this.packageTypes,
        this.dosageUnits
      );

    // if a dosage type has been created successfully, 
    // add it to the list of dosage types
    if (newMedicineDosageType) {
      this.medicineDosageTypes.push(newMedicineDosageType);
      // clone clone clone 

    }
  }



  async save() {
    if (!this.medicine) return;
    const payload: EditMedicinePayload = {
      name: this.medicineForm.value.name,
      description: this.medicineForm.value.description,
      active_ingredient: this.medicineForm.value.activeIngredient,
      active_ingredient_nappy_code: this.medicineForm.value.activeIngredientNappyCode,
      schedule_status: this.medicineForm.value.scheduleStatus,
      barcode_prefix: this.medicineForm.value.barcodePrefix,
      can_order: this.medicineForm.value.canOrder
    }
    const success: boolean = await this._medicineService
      .editMedicine(this.medicine.id, payload);

    if (success) {
      this._dirtinessService.setDirty(false);
      this.medicine = await this._medicineService
        .getMedicine(this.medicine.id);
      if (this.medicine)
        this.medicineDosageTypes = await this._medicineService
          .getMedicineDosageTypes(this.medicine.id, 1, 10, '')
      this.updateView(this.medicine, this.medicineDosageTypes);
    }
  }

  formatTime(time: string | null) {
    if (!time) return "-";
    return formatDistance(new Date(time), new Date(), { addSuffix: true });
  }

  async delete() {

    if (!this.medicine) return;

    const success: boolean = await this._medicineService
      .deleteMedicine(this.medicine.id);

    if (success) {
      this._router.navigate(['/portal/medicine']);
    }
  }

  // Navigate back to medicine list with preserved search
  backToList() {
    const search = this._route.snapshot.queryParamMap.get('search') || undefined;
    this._router.navigate(['/portal/medicine'], {
      queryParams: { search }
    });
  }

}
